export type OptionsArrayKeys<
  T extends OptionsArray<any[], K, any, any>,
  K extends string = 'key',
> = T[number][K];

export interface OptionsArrayOptions<
  K extends string,
  L extends string,
  V extends string,
> {
  keyField: K;
  labelField: L;
  valueField: V;
  enumerable: boolean | string[];
}

export class OptionsArray<
  T extends ({ [field in K]: string | number } & { [field in L | V]?: unknown })[],
  K extends string = 'key',
  L extends string = 'label',
  V extends string = 'value',
  I extends T[number] = T[number] & {
    [field in V]: V extends keyof T[number] ? T[number][V] : string;
  },
> extends Array<I> {
  private readonly _options!: OptionsArrayOptions<K, L, V>;
  private readonly _cacheByKey!: Record<I[K], I | undefined>;
  private readonly _cacheByValue!: Record<I[V], I | undefined>;
  constructor(initialArray: T, options?: Partial<OptionsArrayOptions<K, L, V>>);
  constructor(...args: any[]) {
    const [initialArray, options] = args;
    if (checkArgs()) {
      super();
      this._setOptions(options);
      this._initCaches();
      this._initArray(initialArray);
    } else {
      return new Array(...args) as any;
    }

    function checkArgs() {
      const optionsType = typeof options;
      return (
        args.length < 3 &&
        Array.isArray(initialArray) &&
        (optionsType === 'undefined' || optionsType === 'object')
      );
    }
  }
  private _setOptions(options?: Partial<OptionsArrayOptions<K, L, V>>) {
    Object.defineProperty(this, '_options', {
      value: {
        keyField: 'key',
        labelField: 'label',
        valueField: 'value',
        enumerable: false,
        ...options,
      },
      enumerable: false,
    });
  }
  private _initCaches() {
    Object.defineProperties(this, {
      _cacheByKey: {
        value: {},
        enumerable: false,
      },
      _cacheByValue: {
        value: {},
        enumerable: false,
      },
    });
  }
  private _initArray(initialArray: T) {
    const {
      _options: { keyField, labelField, valueField, enumerable },
    } = this;

    const enumerableHandler: (
      o: PropertyDescriptorMap,
      k: string
    ) => PropertyDescriptorMap = Array.isArray(enumerable)
      ? (o, k) => {
          o[k] = {
            writable: true,
            enumerable: enumerable.includes(k),
          };
          return o;
        }
      : (o, k) => {
          o[k] = {
            writable: true,
            enumerable,
          };
          return o;
        };

    const initialArray_ = initialArray.map((item) => {
      const {
        [keyField]: key,
        [labelField]: label,
        [valueField]: value,
        ...other
      } = item;

      const item_: any = Object.defineProperties(
        item,
        Object.keys(other).reduce(enumerableHandler, {})
      );

      if (!(labelField in item)) {
        item_[labelField] = key;
      }

      if (!(valueField in item)) {
        item_[valueField] = key;
      }

      return item_;
    });

    this.push(...initialArray_);
  }

  // Find
  findByKey(key: I[K]): I | undefined {
    const {
      _cacheByKey,
      _options: { keyField },
    } = this;
    return key in _cacheByKey
      ? _cacheByKey[key]
      : (_cacheByKey[key] = this.find((item) => item[keyField] === key));
  }
  findByValue(value: I[V]): I | undefined {
    const {
      _cacheByValue,
      _options: { valueField },
    } = this;
    return value in _cacheByValue
      ? _cacheByValue[value]
      : (_cacheByValue[value] = this.find((item) => item[valueField] === value));
  }

  // Find label
  findLabelByKey(key: I[K]): I[L] | undefined {
    const { labelField } = this._options;
    return this.findByKey(key)?.[labelField];
  }
  findLabelByValue(value: I[V]): I[L] | undefined {
    const { labelField } = this._options;
    return this.findByValue(value)?.[labelField];
  }

  // Find value
  findValue(key: I[K]) {
    const { valueField } = this._options;
    return this.findByKey(key)?.[valueField];
  }

  // Equality
  is(keys: I[K] | I[K][], value: unknown) {
    return Array.isArray(keys)
      ? keys.some((key) => this.findValue(key) === value)
      : this.findValue(keys) === value;
  }
  not(keys: I[K] | I[K][], value: unknown) {
    return !this.is(keys, value);
  }
}
